import { FC, ReactNode, useState, useEffect, Suspense } from 'react'
import { Avatar, MenuProps, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from './services/supabase'
import {
  LogoutOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useAuth } from './hooks/useAuth'
import { LoginModal } from './components/auth/LoginModal'
import { RegisterModal } from './components/auth/RegisterModal'
import { ForgotPasswordModal } from './components/auth/ForgotPasswordModal'
import { ChangePasswordModal } from './components/auth/ChangePasswordModal'
import { ResetPasswordModal } from './components/auth/ResetPasswordModal'
import { GoogleOneTap } from './components/auth/GoogleOneTap'
import { detectResetPasswordParams, clearResetPasswordParams, validateResetToken } from './utils/urlParams'
import UnderDevelopmentModal from './components/ui/UnderDevelopmentModal'
import Loading from './components/ui/Loading'
import { AppShellProvider } from './contexts/AppShellContext'

// Lazy load components
import { Helmet } from 'react-helmet-async'
import { AppPage, getPageFromPath, pageUrls } from './routing/appRoutes'
import { getFallbackPageTitle } from './routing/pageMetadata'
import HomePage from './pages/home/HomePage'
import './styles/app-shell.css'
import './styles/layout-shell.css'
import AppHeader from './components/layout/AppHeader'
import AppFooter from './components/layout/AppFooter'

interface AppProps {
  children?: ReactNode
}

const App: FC<AppProps> = ({ children }) => {
  const { t, i18n } = useTranslation(['common', 'navigation', 'pages'])
  const { user, loading: authLoading, signOut, signInWithGoogleIdToken } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const currentPage = getPageFromPath(location.pathname)
  const pageTitle = getFallbackPageTitle(currentPage, t)

  const [loginVisible, setLoginVisible] = useState(false)
  const [registerVisible, setRegisterVisible] = useState(false)
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false)
  const [changePasswordVisible, setChangePasswordVisible] = useState(false)
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false)
  const [resetPasswordParams, setResetPasswordParams] = useState<any>(null)
  const [underDevelopmentVisible, setUnderDevelopmentVisible] = useState(false)
  const [selectedFeature] = useState<string>('')
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const [languageMenuVisible, setLanguageMenuVisible] = useState(false)

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    setLanguageMenuVisible(false)
  }

  const handleLogin = () => {
    setLoginVisible(true)
    setRegisterVisible(false)
  }

  const handleRegister = () => {
    setRegisterVisible(true)
    setLoginVisible(false)
  }

  const handleLogout = async () => {
    await signOut()
  }

  const handleNavigate = (page: string) => {
    // 处理带锚点的导航
    if (page.includes('#')) {
      const [pageName, anchor] = page.split('#')

      // 如果已经在目标页面，直接滚动
      if (currentPage === pageName) {
        setTimeout(() => {
          const element = document.getElementById(anchor)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
        return
      }
    }

    const pageName = page.includes('#') ? page.split('#')[0] : page
    const anchor = page.includes('#') ? page.split('#')[1] : null

    const targetUrl = pageUrls[pageName as AppPage]

    if (targetUrl) {
      navigate(targetUrl)
      // 重置滚动位置到顶部
      window.scrollTo(0, 0)

      // 如果有锚点，延迟滚动
      if (anchor) {
        setTimeout(() => {
          const element = document.getElementById(anchor)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
      }
    }
  }

  const handleStartLearning = () => {
    if (user) {
      // 用户已登录，跳转到拼音学习页面
      handleNavigate('pinyin-module')
    } else {
      // 用户未登录，显示注册弹窗
      handleRegister()
    }
  }

  const getPinyinSubmenuItems = () => [
    {
      key: 'pinyin-module',
      label: t('navigation:pinyinSubmenu.pinyinModule'),
      onClick: () => handleNavigate('pinyin-module')
    },
    {
      key: 'pinyin-learning',
      label: t('navigation:pinyinSubmenu.pinyinLearning'),
      onClick: () => handleNavigate('pinyin-learning')
    },
    {
      key: 'tone-learning',
      label: t('navigation:pinyinSubmenu.toneLearning'),
      onClick: () => handleNavigate('tone-learning')
    },
    {
      key: 'pinyin-table',
      label: t('navigation:pinyinSubmenu.pinyinTable'),
      onClick: () => handleNavigate('pinyin-table')
    },
    {
      key: 'pinyin-index',
      label: t('navigation:pinyinSubmenu.pinyinChart'),
      onClick: () => handleNavigate('pinyin-index')
    }
  ]

  const getChengyuSubmenuItems = () => [
    {
      key: 'chengyu-learning',
      matchKeys: ['chengyu-learning', 'chengyu-stage', 'chengyu-idiom'],
      label: t('navigation:chengyuSubmenu.chengyuLearning'),
      onClick: () => handleNavigate('chengyu-learning')
    },
    {
      key: 'chengyu-flashcards',
      label: t('navigation:chengyuSubmenu.chengyuFlashcards'),
      onClick: () => handleNavigate('chengyu-flashcards')
    },
    {
      key: 'chengyu-dictionary',
      label: t('navigation:chengyuSubmenu.chengyuDictionary'),
      onClick: () => handleNavigate('chengyu-dictionary')
    },
    {
      key: 'chengyu-games',
      matchKeys: ['chengyu-games', 'chengyu-game-puzzle', 'chengyu-game-fill', 'chengyu-game-solitaire'],
      label: t('navigation:chengyuSubmenu.chengyuGames'),
      onClick: () => handleNavigate('chengyu-games')
    }
  ]


  const getCharacterSubmenuItems = () => [
    {
      key: 'character-goal',
      matchKeys: ['character-goal', 'character-goal-learn', 'character-goal-review', 'vocabulary-book', 'learned-words', 'mastered-words'],
      label: t('navigation:characterSubmenu.goalDriven'),
      onClick: () => handleNavigate('character-goal')
    },
    {
      key: 'character-free',
      matchKeys: ['character-free', 'character-recognize', 'character-recognize-level', 'character-recognize-detail', 'character-handwrite', 'character-handwrite-level', 'character-handwrite-detail'],
      label: t('navigation:characterSubmenu.freeLearn'),
      onClick: () => handleNavigate('character-free')
    }
  ]



  const handleForgotPassword = () => {
    setForgotPasswordVisible(true)
    setLoginVisible(false)
  }

  const handleChangePassword = () => {
    setChangePasswordVisible(true)
  }

  const handleBackToLogin = () => {
    setLoginVisible(true)
    setForgotPasswordVisible(false)
  }

  const handleResetPasswordClose = () => {
    setResetPasswordVisible(false)
    setResetPasswordParams(null)
    clearResetPasswordParams()
  }

  // 处理 OAuth 回调
  useEffect(() => {
    if (location.pathname === '/auth/callback') {
      // Supabase 需要从 URL hash 中解析 token (如 #access_token=...&refresh_token=...)
      // 必须等待 Supabase 完成 session 建立后才能清理 URL
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: string, session: any) => {
        if (event === 'SIGNED_IN' && session) {
          // Supabase 已成功从 URL hash 解析 token 并建立 session
          // 现在可以安全地清理 URL 并导航到首页
          navigate('/', { replace: true })
          subscription.unsubscribe()
        }
      })

      // 设置超时保护：如果 5 秒内没有收到 SIGNED_IN 事件，也清理 URL
      const timeoutId = setTimeout(() => {
        navigate('/', { replace: true })
        subscription.unsubscribe()
      }, 5000)

      return () => {
        clearTimeout(timeoutId)
        subscription.unsubscribe()
      }
    }
  }, [location.pathname, navigate])

  // 处理密码重置参数
  useEffect(() => {
    const params = detectResetPasswordParams()

    if (params.hasResetToken) {
      setResetPasswordParams(params)

      if (params.isResetPasswordFlow) {
        // 这是重置密码流程，但可能还没有 access_token
        // 需要等待Supabase的认证状态变化
        setTimeout(() => {
          // 延迟一下，让Supabase有时间处理认证状态
          const updatedParams = detectResetPasswordParams()
          if (validateResetToken(updatedParams.accessToken)) {
            setResetPasswordParams(updatedParams)
            setResetPasswordVisible(true)
          } else if (updatedParams.error) {
            setResetPasswordParams(updatedParams)
            setResetPasswordVisible(true)
          } else {
            // 没有access_token，可能是直接访问重置链接
            setResetPasswordVisible(true)
          }
          clearResetPasswordParams()
        }, 1000)
      } else if (validateResetToken(params.accessToken)) {
        // 有有效的access_token
        setResetPasswordVisible(true)
        clearResetPasswordParams()
      } else if (params.error) {
        // 有错误信息
        setResetPasswordVisible(true)
        clearResetPasswordParams()
      }
    }
  }, [])

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0)
    }
  }, [location.pathname, location.hash])

  // Google Analytics Page Tracking
  useEffect(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname,
      })
    }
  }, [location.pathname])


  const userMenuItems: MenuProps['items'] = user ? [
    {
      key: 'profile',
      label: (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{user.email}</span>
        </Space>
      ),
      disabled: true
    },
    {
      type: 'divider'
    },
    {
      key: 'change-password',
      label: (
        <Space>
          <LockOutlined />
          <span>{i18n.language === 'en' ? 'Change Password' : '修改密码'}</span>
        </Space>
      ),
      onClick: handleChangePassword
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          <span>{i18n.language === 'en' ? 'Logout' : '退出登录'}</span>
        </Space>
      ),
      onClick: handleLogout
    }
  ] : []

  const languageMenuItems: MenuProps['items'] = [
    {
      key: 'zh',
      label: '中文',
      onClick: () => changeLanguage('zh')
    },
    {
      key: 'en',
      label: 'English',
      onClick: () => changeLanguage('en')
    }
  ]

  return (
    <AppShellProvider
      value={{
        openLogin: handleLogin,
        openRegister: handleRegister,
        startLearning: handleStartLearning,
      }}
    >
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Google One Tap 登录 */}
      <GoogleOneTap user={user} loading={authLoading} signInWithGoogleIdToken={signInWithGoogleIdToken} />

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={t('common:meta.description')} />
        <html lang={i18n.language} />
      </Helmet>
      <AppHeader
        currentPage={currentPage}
        user={user}
        pinyinItems={getPinyinSubmenuItems()}
        chengyuItems={getChengyuSubmenuItems()}
        characterItems={getCharacterSubmenuItems()}
        languageMenuItems={languageMenuItems}
        userMenuItems={userMenuItems}
        mobileMenuVisible={mobileMenuVisible}
        languageMenuVisible={languageMenuVisible}
        onMobileMenuVisibleChange={setMobileMenuVisible}
        onLanguageMenuVisibleChange={setLanguageMenuVisible}
        onNavigate={handleNavigate}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onChangePassword={handleChangePassword}
      />

      <main className="site-main">
        <Suspense fallback={<Loading size="large" fullscreen={false} className="min-h-[60vh]" />}>
          {children ?? (currentPage === 'home' ? <HomePage onStartLearning={handleStartLearning} /> : null)}
        </Suspense>
      </main>

      <AppFooter languageMenuItems={languageMenuItems} onNavigate={handleNavigate} />

      {/* Login Modal */}
      <LoginModal
        visible={loginVisible}
        onClose={() => setLoginVisible(false)}
        onSwitchToRegister={handleRegister}
        onSwitchToForgotPassword={handleForgotPassword}
      />

      {/* Register Modal */}
      <RegisterModal
        visible={registerVisible}
        onClose={() => setRegisterVisible(false)}
        onSwitchToLogin={handleLogin}
      />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        visible={forgotPasswordVisible}
        onClose={() => setForgotPasswordVisible(false)}
        onBackToLogin={handleBackToLogin}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        visible={changePasswordVisible}
        onClose={() => setChangePasswordVisible(false)}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        visible={resetPasswordVisible}
        onClose={handleResetPasswordClose}
        hasResetToken={!!resetPasswordParams?.accessToken}
      />

      {/* Under Development Modal */}
      <UnderDevelopmentModal
        visible={underDevelopmentVisible}
        onClose={() => setUnderDevelopmentVisible(false)}
        featureName={selectedFeature}
      />
    </div>
    </AppShellProvider>
  )
}

export default App
